import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useParams } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Home, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { t, i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };

  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentInfo(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else {
          // Continue polling
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-24 pb-20 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-md mx-auto px-4 text-center">
        {status === "loading" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            </div>
            <h1 className="font-serif text-2xl font-bold">
              {t("booking.paymentPending")}
            </h1>
            <p className="text-muted-foreground">
              {lang === "fr"
                ? "Veuillez patienter pendant que nous vérifions votre paiement..."
                : "Please wait while we verify your payment..."}
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-green-600">
              {t("booking.paymentSuccess")}
            </h1>
            <p className="text-muted-foreground">
              {t("booking.confirmationMessage")}
            </p>

            {paymentInfo && (
              <div className="bg-muted rounded-xl p-4 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">{t("booking.total")}</span>
                  <span className="font-bold">
                    €{(paymentInfo.amount_total / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Link to={localizedLink("/")}>
                <Button className="w-full rounded-full" data-testid="back-home-btn">
                  <Home className="mr-2 h-4 w-4" />
                  {t("nav.home")}
                </Button>
              </Link>
              <Link to={localizedLink("/contact")}>
                <Button variant="outline" className="w-full rounded-full">
                  <Mail className="mr-2 h-4 w-4" />
                  {t("nav.contact")}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-red-600">
              {t("booking.paymentError")}
            </h1>
            <p className="text-muted-foreground">
              {lang === "fr"
                ? "Une erreur est survenue lors du paiement. Veuillez réessayer ou nous contacter."
                : "An error occurred during payment. Please try again or contact us."}
            </p>

            <div className="flex flex-col gap-3">
              <Link to={localizedLink("/circuits")}>
                <Button className="w-full rounded-full" data-testid="retry-btn">
                  {t("common.retry")}
                </Button>
              </Link>
              <Link to={localizedLink("/contact")}>
                <Button variant="outline" className="w-full rounded-full">
                  <Mail className="mr-2 h-4 w-4" />
                  {t("nav.contact")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
